import {CustomModal} from '_metronic/partials/modals/CustomModal'
import {FC, useEffect, useState} from 'react'
import Searchbar from 'app/components/Searchbar'
import Select from '../Select'
import {useAuth} from 'app/modules/auth'
import {ControlDataType} from '../Controls'
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  writeBatch,
  addDoc,
  serverTimestamp,
} from '@firebase/firestore'
import db from 'services/firebase'
import Button from 'app/components/Button'
import {toAbsoluteUrl} from '_metronic/helpers'
import {showSuccessToast} from 'app/components/ShowToast'
import {SingleValue} from 'react-select'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  handleSetAddEditCustomControl: (control: ControlDataType) => void
  handleSetDeleteCustomControl: (control: ControlDataType) => void
  handleSetAddCSV: () => void
}

const SelectedControlsModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  handleSetAddEditCustomControl,
  handleSetDeleteCustomControl,
  handleSetAddCSV,
}) => {
  const {currentUser} = useAuth()

  const [searchTerm, setSearchTerm] = useState('')
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState(true)
  const [allSelected, setAllSelected] = useState(false)

  const [loading, setLoading] = useState(false)

  const [controls, setControls] = useState<ControlDataType[]>([])
  const [filteredControls, setFilteredControls] = useState<ControlDataType[]>([])
  const [initiallySelectedControls, setInitiallySelectedControls] = useState<Array<string>>([])
  const [selectedControls, setSelectedControls] = useState<Array<string>>([])

  const [controlsCategory, setControlsCategory] = useState<Array<{label: string; value: string}>>(
    []
  )
  const [selectedControlCategory, setSelectedControlCategory] = useState<{
    label: string
    value: string
  } | null>()

  const getControls = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const controlsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'controls',
        'all_controls'
      )

      const queryRef = query(controlsRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const controls = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as ControlDataType[]

        setControls(controls)
        setFilteredControls(controls)

        const selectedControlsIds = controls
          .filter((control) => control?.isShownInTrustCenter)
          .map((control) => control.id)

        setInitiallySelectedControls(selectedControlsIds)
        setSelectedControls(selectedControlsIds)

        const categorizedControlsObj: Record<string, ControlDataType[]> = controls.reduce(
          (acc, control) => {
            const cateogry = control.category || 'Custom'
            if (!acc[cateogry]) {
              acc[cateogry] = []
            }
            acc[cateogry].push(control)
            return acc
          },
          {} as Record<string, ControlDataType[]>
        )

        const categorizedControlsArr: {category: string; controls: ControlDataType[]}[] =
          Object.entries(categorizedControlsObj)
            .map(([category, controls]) => ({
              category,
              controls,
            }))
            .sort((a, b) => a.category.localeCompare(b.category))

        setControlsCategory(
          categorizedControlsArr.map((list) => ({
            label: list.category,
            value: list.category,
          }))
        )
      })

      return unsubscribe
    } catch (error) {
      console.error('Errow while getting controls for trust center', error)
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(false)
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    getControls()
  }, [])

  useEffect(() => {
    let filteredData = controls.filter((control) =>
      control.title.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (selectedControlCategory?.value) {
      filteredData = filteredData.filter(
        (control) => control.category.toLowerCase() === selectedControlCategory?.value.toLowerCase()
      )
      if (filteredData.length > 0) {
        setAllSelected(filteredData.every((control) => selectedControls.includes(control.id)))
      }
    } else {
      if (controls.length > 0) {
        setAllSelected(controls.every((control) => selectedControls.includes(control.id)))
      }
    }

    setFilteredControls(filteredData)
    setSearchLength(filteredData.length === 0)
  }, [searchTerm, selectedControlCategory, controls])

  const toggleSelectedControls = (controlId: string) => {
    const currentSelectedControls = selectedControls
    const isFound = currentSelectedControls.some((id) => id === controlId)

    if (isFound) {
      setSelectedControls((prev) => prev.filter((id) => id !== controlId))
      setAllSelected(false)
    } else {
      setSelectedControls((prev) => [...prev, controlId])
      setAllSelected(
        filteredControls.every((control) => [...selectedControls, controlId].includes(control.id))
      )
    }
  }

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked
    const idsArr = filteredControls.map((control) => control.id)
    setAllSelected(isChecked)
    setSelectedControls((prev) =>
      isChecked
        ? [...prev, ...idsArr.filter((controlId) => !prev.includes(controlId))]
        : [...prev.filter((controlId) => !idsArr.includes(controlId))]
    )
  }

  const okBtnClickHandler = async () => {
    setLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenanRef = doc(db, 'tenants', tenantId)
      const batch = writeBatch(db)

      for (const controlId of selectedControls) {
        const controlRef = doc(tenanRef, 'trust_center', 'controls', 'all_controls', controlId)

        if (!initiallySelectedControls.includes(controlId)) {
          batch.update(controlRef, {
            isShownInTrustCenter: true,
          })
        }
      }

      for (const id of initiallySelectedControls) {
        const controlRef = doc(tenanRef, 'trust_center', 'controls', 'all_controls', id)

        if (!selectedControls.includes(id)) {
          batch.update(controlRef, {
            isShownInTrustCenter: false,
          })
        }
      }

      await batch.commit()

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `Controls selection for trust center has been updated`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'Controls selection udpated',
      })
      showSuccessToast({message: 'Controls selection has been updated successfully'})
    } catch (error) {
    } finally {
      setLoading(false)
      hideModal()
    }
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title='Selected Controls for Trust Center'
      isCloseIconShown={true}
      okBtnText='Save'
      okBtnClickHandler={okBtnClickHandler}
      isLoading={loading}
      isLoadingBtnText='Saving...'
      dialogClassName={'modal-width-lg'}
    >
      <div className='d-flex' style={{gap: '12px', marginBottom: '20px'}}>
        <div className='d-none d-md-block flex-fill'>
          <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} width='100%' />
        </div>

        <div className='flex-fill' style={{maxWidth: '300px'}}>
          <Select
            placeholder='Category'
            options={controlsCategory}
            value={selectedControlCategory}
            onChange={(value) => {
              setSelectedControlCategory(value as SingleValue<{label: string; value: string}>)
            }}
          />
        </div>

        <div className='d-flex align-items-center ms-auto' style={{gap: '12px'}}>
          <Button onClick={handleSetAddCSV}>
            <img
              alt='upload-icon'
              src={toAbsoluteUrl('/media/icon/upload.svg')}
              className='me-2'
              style={{height: '18px', width: '18px'}}
            />
            <span>Import</span>
          </Button>

          <Button
            onClick={() => {
              handleSetAddEditCustomControl({id: '', title: '', category: '', description: ''})
            }}
          >
            <img
              alt='plus-icon'
              src={toAbsoluteUrl('/media/icon/plus.svg')}
              className='me-2'
              style={{height: '18px', width: '18px'}}
            />
            <span>Add New</span>
          </Button>
        </div>
      </div>

      <div
        className='table-responsive'
        style={{
          borderRadius: '6.175px',
          border: '1px solid var(--bs-border-color)',
          maxHeight: '45vh',
          overflowY: 'auto',
        }}
      >
        <table className='table table-rounded gy-7 gs-7 mb-0 align-middle'>
          <thead>
            <tr
              className='fw-bold'
              style={{
                color: '#5A5A5A',
                boxShadow: 'inset 0 -1px 0 var(--bs-border-color)',
                backgroundColor: '#F9FAFB',
                position: 'sticky',
                top: 0,
                zIndex: '10',
              }}
            >
              <th className='min-w-25px'>
                <div className='form-check form-check-sm form-check-custom form-check-solid'>
                  <input
                    className='form-check-input form-check-primary cursor-pointer'
                    type='checkbox'
                    checked={allSelected}
                    data-kt-check='true'
                    data-kt-check-target='.widget-9-check'
                    onChange={handleSelectAllChange}
                  />
                </div>
              </th>

              <th className='min-w-200px min-w-xxl-300px'>CONTROL</th>
              <th className='min-w-200px'>DESCRIPTION</th>
              <th className='min-w-150px min-w-xxl-250px'>CATEGORY</th>
            </tr>
          </thead>

          <tbody>
            {showEmptyState ? (
              <tr>
                <td colSpan={4}>
                  <div
                    className='d-flex justify-content-center align-items-center text-center'
                    style={{height: '245px'}}
                  >
                    <div className='spinner-border mb-2 ' role='status' style={{color: '#0E9F6E'}}>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : filteredControls.length > 0 ? (
              filteredControls.map((control, index) => (
                <>
                  <tr
                    style={{
                      borderBottom: `${
                        index !== filteredControls.length - 1
                          ? '1px solid var(--bs-border-color)'
                          : ''
                      }`,
                    }}
                    key={control.id}
                  >
                    <td>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input widget-9-check cursor-pointer form-check-primary'
                          type='checkbox'
                          value={control.id}
                          checked={selectedControls.includes(control.id)}
                          onChange={() => toggleSelectedControls(control.id)}
                        />
                      </div>
                    </td>

                    <td>
                      <span className='text-dark fs-6 fw-medium'>{control.title}</span>
                    </td>

                    <td>
                      <p className='text-dark mb-0 text-truncate-multiline fs-6'>
                        {control.description.replace(/<\/?[^>]+(>|$)/g, '')}
                      </p>
                    </td>

                    <td>
                      <div
                        className='d-flex flex-column flex-md-row align-items-md-center justify-content-between'
                        style={{fontSize: '14px', gap: '6px'}}
                      >
                        <span className='text-dark fs-6' style={{wordBreak: 'break-word'}}>
                          {control.category}
                        </span>

                        {control.isCustom && (
                          <div className='dropdown mx-2' style={{position: 'relative'}}>
                            <Button
                              className='position-relative'
                              style={{height: '34px', width: '34px'}}
                              id='dropdownMenu2'
                              data-bs-toggle='dropdown'
                              aria-haspopup='true'
                              aria-expanded='false'
                            >
                              <img
                                alt='dot-icon'
                                src={toAbsoluteUrl('/media/icon/dots.svg')}
                                className=''
                                style={{height: '18px', width: '18px'}}
                              />
                            </Button>

                            <ul
                              className='dropdown-menu'
                              aria-labelledby='dropdownMenu2'
                              style={{
                                border: '1px solid #E5E5E5',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                borderRadius: '8px',
                              }}
                            >
                              <li
                                style={{
                                  borderBottom: '1px solid #E5E5E5',
                                }}
                              >
                                <button
                                  className='dropdown-item d-flex align-items-center'
                                  style={{color: '#262626', gap: '12px', padding: '12px 16px'}}
                                  type='button'
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleSetAddEditCustomControl(control)
                                  }}
                                >
                                  <img
                                    alt='pencil-icon'
                                    src={toAbsoluteUrl('/media/icon/pencil.svg')}
                                    className=''
                                    style={{height: '18px', width: '18px'}}
                                  />
                                  <span>Edit Control</span>
                                </button>
                              </li>

                              <li>
                                <button
                                  className='dropdown-item d-flex align-items-center'
                                  style={{color: '#E02424', gap: '12px', padding: '10px 16px'}}
                                  type='button'
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleSetDeleteCustomControl(control)
                                  }}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  >
                                    <path d='M4 7l16 0' />
                                    <path d='M10 11l0 6' />
                                    <path d='M14 11l0 6' />
                                    <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                    <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                  </svg>

                                  <span>Delete Control</span>
                                </button>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>

                  {filteredControls.length === 1 && (
                    <tr>
                      <td colSpan={4}>
                        <div style={{height: '55px'}}></div>
                      </td>
                    </tr>
                  )}
                </>
              ))
            ) : (
              <tr>
                <td colSpan={4}>
                  <div
                    className='w-100 d-flex align-items-center justify-content-center'
                    style={{height: '245px'}}
                  >
                    {searchLength ? (
                      <div className='d-flex flex-column align-items-center gap-4'>
                        <img src='/media/buttons/emptystate.svg' alt='' />
                        <p className='mb-0' style={{fontWeight: '600'}}>
                          No controls found
                        </p>
                        <p>
                          We couldn't find any controls that match your search. Maybe try a
                          different search!
                        </p>
                      </div>
                    ) : (
                      <div
                        className='w-100 d-flex justify-content-center align-items-center'
                        style={{height: '100%'}}
                      >
                        <div className='d-flex flex-column align-items-center gap-4'>
                          <div
                            className='d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: '#F3F4F6',
                              borderRadius: '12px',
                              height: '48px',
                              width: '48px',
                            }}
                          >
                            <img
                              alt='adjustments-horizontal-icon'
                              src={toAbsoluteUrl('/media/icon/adjustments-horizontal.svg')}
                              className=''
                              style={{height: '32px', width: '32px'}}
                            />
                          </div>

                          <span className='text-dark fs-6 fw-medium'>No controls added</span>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </CustomModal>
  )
}
export default SelectedControlsModal
